import { computed, reactive, ComputedRef, unref } from 'vue';
import { ConditionPickerOption, Operation, PickerOption } from '@shared/components/conditions/models/conditions';
import { ConditionViewModel } from '@shared/components/conditions/models/conditionViewModel';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { ConditionComponents } from '@shared/components/conditions/configComponents/conditionComponents';
import { ConditionDataType, Conditions } from '@shared/components/conditions/models/conditions';
import AttributeIconUtil from '@shared/util/attributeIconUtil';
import { store } from '@/store';

export function useConditions(
  cachedAttributes: ContactAttributeViewModel[] = [],
  overrideConditions?: ComputedRef<ConditionPickerOption[]>,
  contextualConditions: ConditionPickerOption[] = [],
) {
  const state = reactive({
    operations: Conditions.Operations,
    dateTimeUnits: Conditions.DateTimeUnits,
    cachedAttributes: cachedAttributes,
  });

  const conditionsFromCache = computed(() => {
    return [
      ...state.cachedAttributes.map(
        (attribute: ContactAttributeViewModel) =>
          ({
            id: getId(attribute),
            title: attribute.name,
            type: attribute.type,
            icon: AttributeIconUtil.getIconType(attribute.type, attribute.id, attribute.system),
          }) as ConditionPickerOption,
      ),
      ...(contextualConditions || []),
    ];
  });

  // Use overrideConditions if provided, otherwise fall back to conditionsFromCache
  const conditions = computed(() => overrideConditions || conditionsFromCache.value || []);
  const visibleConditions = computed(() => conditions.value);

  const getConfigComponent = (condition: ConditionViewModel) => {
    if (condition.type === ConditionDataType.DateTime || condition.type === ConditionDataType.Date) {
      return ConditionComponents.DateTimeCondition;
    } else if (condition.type === ConditionDataType.Text) {
      return ConditionComponents.TextCondition;
    } else if (condition.type === ConditionDataType.Number) {
      return ConditionComponents.NumberCondition;
    } else if (condition.type === ConditionDataType.Decimal) {
      return ConditionComponents.DecimalCondition;
    } else if (condition.type === ConditionDataType.List) {
      return ConditionComponents.ListCondition;
    } else if (condition.type === ConditionDataType.Duration) {
      return ConditionComponents.DurationCondition;
    } else if (condition.type === ConditionDataType.Intent) {
      return ConditionComponents.IntentCondition;
    }
  };

  const getConditionOptions = (condition: ConditionViewModel): any => {
    return '';
  };

  const getListConditionValueTitle = (condition: ConditionViewModel) => {
    return condition.value;
  };

  const getIntentConditionValueTitle = (condition: ConditionViewModel) => {
    const intentId = condition.value;
    return store.getters.getIntentName(intentId) || intentId;
  };

  const isConditionValid = (condition: ConditionViewModel): boolean => {
    if (condition.attribute.startsWith('appUser.customAttributes')) {
      const attributeName = condition.attribute.substring(condition.attribute.lastIndexOf('.') + 1);
      const attribute = state.cachedAttributes.find((attr: ContactAttributeViewModel) => attr.id === attributeName);
      return !condition.invalid && !!attribute && !attribute.archived;
    }
    return !condition.invalid;
  };

  const getFormattedDescription = (condition: ConditionViewModel) => {
    if (condition.invalid) {
      return `${getAttribute(condition)} is invalid`.trim();
    }
    return `${getAttribute(condition)} ${getOperation(condition)} ${getValue(condition)} ${getUnit(condition)}`.trim();
  };

  const getAttribute = (condition: ConditionViewModel) => {
    const matchedAttribute = unref(conditions.value).find((c: ConditionPickerOption) => c.id === condition.attribute);
    if (matchedAttribute) {
      return matchedAttribute.title;
    } else if (condition.attribute.includes('.')) {
      return condition.attribute.substring(condition.attribute.lastIndexOf('.') + 1);
    }
    return condition.attribute;
  };

  const getOperation = (condition: ConditionViewModel) => {
    const typedOperations = state.operations[condition.type];
    if (!typedOperations) return '...';

    const matchedOperation = typedOperations.find(
      (o: Operation) => o.operation === condition.operation && o.result === condition.result,
    );
    return matchedOperation ? matchedOperation.title : '...';
  };

  const getValue = (condition: ConditionViewModel) => {
    if (condition.type === ConditionDataType.List) {
      return getListConditionValueTitle(condition);
    } else if (condition.type === ConditionDataType.Intent) {
      return getIntentConditionValueTitle(condition);
    }
    return condition.value;
  };

  const getUnit = (condition: ConditionViewModel) => {
    if (condition.unit && condition.operation !== 'isSet') {
      if (condition.type === ConditionDataType.Duration || condition.type === 'seconds') {
        return condition.unit;
      }
      const dateTime = state.dateTimeUnits.find((u: PickerOption) => u.id === condition.unit);
      if (dateTime) {
        let title = dateTime.title;
        if (condition.value === '1') {
          title = dateTime.title.slice(0, -1);
        }
        return `${title} ago`;
      }
    }
    return '';
  };

  return {
    conditions,
    visibleConditions,
    getConfigComponent,
    getConditionOptions,
    getListConditionValueTitle,
    getIntentConditionValueTitle,
    isConditionValid,
    getFormattedDescription,
    getAttribute,
    getOperation,
    getValue,
    getUnit,
    getId,
  };
}

function getId(attribute: ContactAttributeViewModel) {
  return attribute.system ? `appUser.${attribute.id}` : `appUser.customAttributes.${attribute.id}`;
}
